// ============================================================================
// CHANGO V5.0 — Ops Legal Agreements console (#40 follow-up, §11.10 + UEDA (一))
// ============================================================================
// Admin-only surface for publishing TOS / 隐私政策 / 计费条款 / 数据出口协议.
//
//   GET  /api/legal/agreements                  list all versions
//   POST /api/legal/agreements/upload (multipart)
//     fields: agreement_type, version, title, effective_from, file
//     accepts: .pdf | .docx | .md | .markdown | text/plain
//
// UX:
//   - Left pane:   list of all agreement rows, grouped by type, is_current
//                  badge, links to original + PDF.
//   - Right pane:  upload form. On success, reloads the list and clears.
//
// §9.6 / §11.10: publishing a new version forces every tenant to re-consent
// on next authed request (server-side agreementEnforcement middleware +
// resetAgreementVersionCache). This page does NOT confirm that — it's a
// silent side-effect of the upload. The blocking modal (task #44) is what
// users see next.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

const AGREEMENT_TYPES = [
  { value: 'tos',         label: '用户服务协议 (TOS)' },
  { value: 'privacy',     label: '隐私政策' },
  { value: 'billing',     label: '计费条款' },
  { value: 'data_export', label: '数据出口协议' },
];

// Mirror ACCEPTED_MIME_TO_EXT on the server — keep in sync by hand until we
// expose a /meta endpoint. Mismatch surfaces as a 400 from the server.
const ACCEPTED_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/markdown',
  'text/plain',
];
const ACCEPTED_EXT = '.pdf,.docx,.md,.markdown,.txt';
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB — matches server LEGAL_UPLOAD_MAX_BYTES

function formatBytes(n) {
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toISOString().slice(0, 19).replace('T', ' ');
  } catch {
    return String(v);
  }
}

function AgreementRow({ row }) {
  const typeLabel = AGREEMENT_TYPES.find((t) => t.value === row.agreement_type)?.label
                    || row.agreement_type;
  return (
    <li className="rounded border border-slate-700 bg-slate-800/60 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-100">{typeLabel}</span>
            <span className="rounded bg-slate-700 px-2 py-0.5 font-mono text-xs text-slate-200">
              {row.version}
            </span>
            {row.is_current ? (
              <span className="rounded bg-emerald-700 px-2 py-0.5 text-xs text-emerald-50">
                当前
              </span>
            ) : null}
            <span className="rounded bg-slate-700 px-2 py-0.5 font-mono text-xs text-slate-300">
              {row.file_type || 'markdown'}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-400">{row.title}</div>
          <div className="mt-1 text-xs text-slate-500">
            生效: {formatDate(row.effective_from)} · 上传: {formatDate(row.created_at)}
            {row.file_name ? <> · 文件: {row.file_name}</> : null}
            {row.file_size_bytes ? <> · {formatBytes(row.file_size_bytes)}</> : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
          {row.file_url ? (
            <a href={row.file_url} target="_blank" rel="noreferrer"
               className="text-chango-300 underline-offset-2 hover:underline">
              原始文件
            </a>
          ) : null}
          {row.file_pdf_url ? (
            <a href={row.file_pdf_url} target="_blank" rel="noreferrer"
               className="text-chango-300 underline-offset-2 hover:underline">
              PDF 预览
            </a>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function UploadForm({ onSuccess }) {
  const [type, setType]         = useState('tos');
  const [version, setVersion]   = useState('');
  const [title, setTitle]       = useState('');
  const [effective, setEffective] = useState(() => new Date().toISOString().slice(0, 10));
  const [file, setFile]         = useState(null);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);
  const [ok, setOk]             = useState(null);
  const fileInputRef = useRef(null);

  // 必须与后端 lib/validate.js:assertAgreementVersion 一致 —
  //   /^[a-z]+-\d{4}-\d{2}-\d{2}$/  (全小写类型前缀 + 短横线 + YYYY-MM-DD)
  // 示例: tos-2026-04-24 / privacy-2026-04-24 / billing-2026-04-24
  const versionOk   = /^[a-z]+-\d{4}-\d{2}-\d{2}$/.test(version.trim());

  // 一键套用"{type}-{effective_date}"到版本号输入框。避免用户手敲出错。
  const suggestedVersion = useMemo(() => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effective || '')) return '';
    return `${type}-${effective}`;
  }, [type, effective]);
  const applySuggestedVersion = () => {
    if (suggestedVersion) setVersion(suggestedVersion);
  };
  const titleOk     = title.trim().length >= 2 && title.trim().length <= 200;
  const effectiveOk = /^\d{4}-\d{2}-\d{2}$/.test(effective);
  const fileOk      = file && file.size > 0 && file.size <= MAX_BYTES;
  const formOk      = versionOk && titleOk && effectiveOk && fileOk && !busy;

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setErr(null);
    if (!f) { setFile(null); return; }
    if (f.size > MAX_BYTES) {
      setErr(new Error(`文件过大 (${formatBytes(f.size)})，最大支持 10 MB`));
      setFile(null);
      return;
    }
    if (!ACCEPTED_MIME.includes(f.type) && !/\.(pdf|docx|md|markdown|txt)$/i.test(f.name)) {
      setErr(new Error(`不支持的文件类型: ${f.type || '未知'}`));
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!formOk) return;
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      const fd = new FormData();
      fd.append('agreement_type', type);
      fd.append('version', version.trim());
      fd.append('title', title.trim());
      fd.append('effective_from', `${effective}T00:00:00Z`);
      fd.append('file', file);
      const res = await api.upload('/api/legal/agreements/upload', fd);
      setOk({ id: res?.agreement?.id, version: res?.agreement?.version });
      // Reset form but keep the `type` preselection for rapid batch uploads.
      setVersion('');
      setTitle('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onSuccess && onSuccess();
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3 rounded border border-slate-700 bg-slate-800/80 p-4">
      <h3 className="text-sm font-semibold text-slate-100">上传新版本</h3>

      <div>
        <label className="block text-xs text-slate-400">协议类型</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-slate-100"
          disabled={busy}
        >
          {AGREEMENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs text-slate-400">版本号</label>
        <div className="mt-1 flex gap-2">
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="例如 tos-2026-04-24 或 privacy-2026-04-24"
            className="flex-1 rounded border border-slate-600 bg-slate-900 px-2 py-1 font-mono text-sm text-slate-100"
            disabled={busy}
            required
          />
          {suggestedVersion && suggestedVersion !== version.trim() ? (
            <button
              type="button"
              onClick={applySuggestedVersion}
              disabled={busy}
              className="shrink-0 rounded border border-chango-600 bg-chango-700/40 px-2 py-1 text-xs text-chango-200 hover:bg-chango-600/40 disabled:opacity-50"
              title={`一键填入 ${suggestedVersion}`}
            >
              用 {suggestedVersion}
            </button>
          ) : null}
        </div>
        {!versionOk && version ? (
          <p className="mt-1 text-xs text-amber-300">
            格式: <code>{'{类型}'}-YYYY-MM-DD</code>，全小写 (如 <code>tos-2026-04-24</code>、<code>privacy-2026-04-24</code>、<code>billing-2026-04-24</code>)
          </p>
        ) : (
          <p className="mt-1 text-xs text-slate-500">
            前缀建议与"协议类型"一致: tos / privacy / billing / data_export
          </p>
        )}
      </div>

      <div>
        <label className="block text-xs text-slate-400">标题</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="例如 长戈 CHANGO 用户服务协议"
          className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-slate-100"
          disabled={busy}
          required
          minLength={2}
          maxLength={200}
        />
      </div>

      <div>
        <label className="block text-xs text-slate-400">生效日期</label>
        <input
          type="date"
          value={effective}
          onChange={(e) => setEffective(e.target.value)}
          className="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-slate-100"
          disabled={busy}
          required
        />
      </div>

      <div>
        <label className="block text-xs text-slate-400">文件 (pdf / docx / md, 最大 10 MB)</label>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXT}
          onChange={onFileChange}
          className="mt-1 block w-full text-xs text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-chango-600 file:px-3 file:py-1 file:text-xs file:text-white hover:file:bg-chango-500"
          disabled={busy}
          required
        />
        {file ? (
          <p className="mt-1 text-xs text-slate-400">
            {file.name} · {formatBytes(file.size)}
          </p>
        ) : null}
      </div>

      {err ? (
        <div className="rounded border border-rose-700/60 bg-rose-900/40 px-3 py-2 text-xs text-rose-200">
          上传失败: {err instanceof HttpError ? `${err.code} — ${err.message}` : err.message}
        </div>
      ) : null}
      {ok ? (
        <div className="rounded border border-emerald-700/60 bg-emerald-900/40 px-3 py-2 text-xs text-emerald-200">
          ✓ 已发布 v{ok.version} (ID #{ok.id}) — 所有租户将在下次访问时被要求重新同意
        </div>
      ) : null}

      <button
        type="submit"
        disabled={!formOk}
        className="rounded bg-chango-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-chango-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
      >
        {busy ? '上传中…' : '发布新版本'}
      </button>
    </form>
  );
}

export default function OpsLegalAgreements() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.get('/api/legal/agreements');
      setRows(Array.isArray(res?.agreements) ? res.agreements : []);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of rows) {
      if (!g.has(r.agreement_type)) g.set(r.agreement_type, []);
      g.get(r.agreement_type).push(r);
    }
    // Sort each group newest first by created_at.
    for (const arr of g.values()) {
      arr.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    }
    return g;
  }, [rows]);

  // Phase 3 RoleGate-B: owner ⊃ admin，两者都允许。owner 在 is_owner（§ST.10 Plan B）
  if (user && user.role !== 'admin' && user.role !== 'owner' && user.is_owner !== true) {
    return (
      <div className="min-h-screen bg-slate-900 p-8 text-slate-100">
        <p className="text-sm text-rose-300">仅管理员可访问此页面。</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">法律协议管理</h1>
            <p className="text-xs text-slate-400">
              发布 TOS / 隐私政策 / 计费条款 · 发布后所有租户需重新同意 (§11.10)
            </p>
          </div>
          <Link to="/ops" className="text-xs text-slate-300 hover:text-white">
            ← 返回 Ops
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-6 p-6 lg:grid-cols-[1fr_360px]">
        <section>
          <h2 className="mb-3 text-sm font-semibold text-slate-100">历史版本</h2>
          {loading ? (
            <p className="text-xs text-slate-400">加载中…</p>
          ) : err ? (
            <div className="rounded border border-rose-700/60 bg-rose-900/40 p-3 text-xs text-rose-200">
              加载失败: {err instanceof HttpError ? err.message : '网络错误'}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-slate-400">尚无任何协议版本 — 请上传第一版。</p>
          ) : (
            <div className="space-y-5">
              {AGREEMENT_TYPES.map((t) => {
                const list = grouped.get(t.value) || [];
                if (list.length === 0) return (
                  <div key={t.value}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t.label}
                    </h3>
                    <p className="text-xs text-slate-500">— 暂无版本 —</p>
                  </div>
                );
                return (
                  <div key={t.value}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t.label} <span className="text-slate-600">({list.length})</span>
                    </h3>
                    <ul className="space-y-2">
                      {list.map((r) => <AgreementRow key={r.id} row={r} />)}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <aside>
          <UploadForm onSuccess={reload} />
          <p className="mt-3 text-xs text-slate-500 leading-5">
            提示: .docx 文件会由 libreoffice 自动转换为 PDF 预览 — 若服务器未安装 libreoffice，原始 .docx 仍会保存，预览将回退到 mammoth.js 渲染。
          </p>
        </aside>
      </main>
    </div>
  );
}
